import Message from 'src/app/models/Message'
import { UnifiedMessageRequest } from '../messageApiClient'

// WhatsApp-specific supported message types
export const WHATSAPP_SUPPORTED_TYPES = ['text', 'image', 'video', 'audio', 'document', 'interactive'] as const
export type WhatsAppMessageType = typeof WHATSAPP_SUPPORTED_TYPES[number]

/**
 * WhatsAppMessageHelper
 *
 * Platform-specific helper for WhatsApp messages.
 * Handles validation and request formatting specific to WhatsApp v2 API.
 */
export class WhatsAppMessageHelper {
  /**
   * Validate message type for WhatsApp platform
   */
  static validateMessageType(messageType: string): boolean {
    return WHATSAPP_SUPPORTED_TYPES.includes(messageType as any)
  }

  /**
   * Get human-readable error for unsupported types
   */
  static getUnsupportedTypeError(messageType: string): string {
    return (
      `Message type '${messageType}' is not supported for WhatsApp. ` +
      `Supported types: ${WHATSAPP_SUPPORTED_TYPES.join(', ')}`
    )
  }

  /**
   * Build WhatsApp-specific request body
   * Formats message data according to WhatsApp v2 API requirements
   */
  static buildRequestBody(
    customerPhoneNumber: string,
    message: Message
  ): UnifiedMessageRequest {
    const requestBody: UnifiedMessageRequest = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',  // Required by WhatsApp Meta API
      to: customerPhoneNumber,
      type: message.messageType!,
    } as UnifiedMessageRequest

    const textContent = typeof message.textContent === 'string'
      ? message.textContent
      : JSON.stringify(message.textContent).replace(/"/g, '')

    // Add content based on message type
    switch (message.messageType) {
      case 'text':
        requestBody.text = {
          body: textContent,  // WhatsApp API expects 'body' not 'text'
          preview_url: message.previewurl ?? false,
        } as any
        break

      case 'image':
        if (!message.mediaUrl) {
          throw new Error('Image URL is required for image messages')
        }
        requestBody.image = {
          link: message.mediaUrl,
        }
        // Add caption if text content exists
        if (textContent) {
          (requestBody.image as any).caption = textContent
        }
        break

      case 'video':
        if (!message.mediaUrl) {
          throw new Error('Video URL is required for video messages')
        }
        requestBody.video = {
          link: message.mediaUrl,
        }
        // Add caption if text content exists
        if (textContent) {
          (requestBody.video as any).caption = textContent
        }
        break

      case 'audio':
        if (!message.mediaUrl) {
          throw new Error('Audio URL is required for audio messages')
        }
        ;(requestBody as any).audio = {
          link: message.mediaUrl,
        }
        break

      case 'document':
        if (!message.mediaUrl) {
          throw new Error('Document URL is required for document messages')
        }
        ;(requestBody as any).document = {
          link: message.mediaUrl,
          filename: message.filename || 'document',
        }
        // Add caption if text content exists
        if (textContent) {
          (requestBody as any).document.caption = textContent
        }
        break

      case 'interactive':
        if (!message.interactive) {
          throw new Error('Interactive content is required for interactive messages')
        }
        ;(requestBody as any).interactive = message.interactive
        break

      default:
        throw new Error(this.getUnsupportedTypeError(message.messageType!))
    }

    // Add reply context if this is a reply
    if (message.replyMessageID?.id) {
      ;(requestBody as any).context = {
        message_id: message.replyMessageID.id.toString(),
      }
    }

    return requestBody
  }

  /**
   * Validate all WhatsApp message requirements
   * Throws error if validation fails
   */
  static validate(message: Message, customerPhoneNumber: string): void {
    // Validate phone number
    if (!customerPhoneNumber || customerPhoneNumber.trim() === '') {
      throw new Error('Customer phone number is required for WhatsApp messages')
    }

    // Validate message type
    if (!message.messageType) {
      throw new Error('Message type is required')
    }

    if (!this.validateMessageType(message.messageType)) {
      throw new Error(this.getUnsupportedTypeError(message.messageType))
    }

    // Validate content based on type
    if (message.messageType === 'text' && !message.textContent) {
      throw new Error('Text content is required for text messages')
    }

    if (['image', 'video', 'audio', 'document'].includes(message.messageType) && !message.mediaUrl) {
      throw new Error(`Media URL is required for ${message.messageType} messages`)
    }

    if (message.messageType === 'interactive' && !message.interactive) {
      throw new Error('Interactive content is required for interactive messages')
    }
  }
}
